import { useEffect, useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { io } from "socket.io-client";
import axios from "axios";
import {
  addAdHocRestaurant,
  setAdHocSearchStatus,
  promptForMoreBatches,
  adHocSearchComplete,
  stopAdHocSearch,
} from "../actions/restaurants";

/**
 * Custom hook for managing WebSocket connection to ad hoc search updates.
 *
 * @param {string} jobId - The ad hoc search job ID to subscribe to
 * @param {object} options - Configuration options
 *   - onRestaurantReceived: Callback when a restaurant is received
 *   - onPrompt: Callback when prompt event is received (20 with 0 results)
 *   - onComplete: Callback when search completes
 *   - autoConnect: Whether to connect automatically (default: true)
 * @returns {object} - { connected, restaurantsProcessed, veganResultsFound }
 */
export function useAdHocSearch(jobId, options = {}) {
  const {
    onRestaurantReceived,
    onPrompt,
    onComplete,
    autoConnect = true,
  } = options;

  const dispatch = useDispatch();
  const [connected, setConnected] = useState(false);
  const [restaurantsProcessed, setRestaurantsProcessed] = useState(0);
  const [veganResultsFound, setVeganResultsFound] = useState(0);

  const socketRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const lastUpdateRef = useRef(null);

  useEffect(function connectAdHocSearchWebSocket() {
    if (!jobId || !autoConnect) {
      return;
    }

    // Connect to WebSocket server with auto-reconnect enabled
    const socket = io(window.location.origin, {
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: Infinity,
    });

    socketRef.current = socket;

    socket.on("connect", () => {
      console.log("WebSocket connected for ad hoc search");
      setConnected(true);

      // Subscribe to job updates
      socket.emit("subscribeJob", jobId);
    });

    socket.on("disconnect", () => {
      console.log("WebSocket disconnected");
      setConnected(false);
    });

    // Listen for ad hoc restaurant results (separate from jobUpdate)
    socket.on("adHocRestaurantResult", (data) => {
      console.log("Ad hoc restaurant result received:", data);

      if (data.restaurant) {
        // Add restaurant to results
        dispatch(addAdHocRestaurant(data.restaurant));

        if (onRestaurantReceived) {
          onRestaurantReceived(data.restaurant);
        }
      }

      // Always update status (even if no restaurant - for count accuracy)
      dispatch(
        setAdHocSearchStatus("processing", data.jobId, {
          restaurantsProcessed: data.restaurantsProcessed,
          veganResultsFound: data.veganResultsFound,
          totalRestaurants: data.totalRestaurants || 20, // Default to 20 if not provided
          progress: data.progress,
          message: data.message,
        })
      );

      setRestaurantsProcessed(data.restaurantsProcessed);
      setVeganResultsFound(data.veganResultsFound);
    });

    // Listen for prompt event (20 processed with 0 results)
    socket.on("adHocSearchPrompt", (data) => {
      console.log("Ad hoc search prompt received:", data);
      dispatch(promptForMoreBatches());

      if (onPrompt) {
        onPrompt(data);
      }
    });

    // Listen for search complete event
    socket.on("adHocSearchComplete", (data) => {
      console.log("Ad hoc search complete:", data);
      dispatch(adHocSearchComplete(data));

      setRestaurantsProcessed(data.totalProcessed);
      setVeganResultsFound(data.veganResultsFound);

      // Stop polling when complete
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }

      if (onComplete) {
        onComplete(data);
      }
    });

    // Listen for search cancelled event
    socket.on("adHocSearchCancelled", (data) => {
      console.log("Ad hoc search cancelled:", data);
      dispatch(stopAdHocSearch());
      dispatch(
        setAdHocSearchStatus("cancelled", data.jobId, {
          message: data.message || "Search cancelled",
        })
      );
      
      // Stop polling when cancelled
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    });

    socket.on("connect_error", (error) => {
      console.error("WebSocket connection error:", error);
      setConnected(false);
    });

    // Polling function to fetch job status as fallback
    const pollJobStatus = async () => {
      try {
        const response = await axios.get(`/api/jobs/${jobId}`);
        const jobData = response.data;
        
        // Update last update time
        lastUpdateRef.current = Date.now();
        
        // Only update if we got valid data
        if (jobData && jobData.status) {
          // Update counts if available (for ad hoc search jobs)
          if (jobData.restaurantsProcessed !== undefined) {
            setRestaurantsProcessed(jobData.restaurantsProcessed);
          }
          if (jobData.veganResultsFound !== undefined) {
            setVeganResultsFound(jobData.veganResultsFound);
          }
          
          // Update status in Redux
          dispatch(
            setAdHocSearchStatus(jobData.status, jobId, {
              restaurantsProcessed: jobData.restaurantsProcessed || 0,
              veganResultsFound: jobData.veganResultsFound || 0,
              totalRestaurants: jobData.totalRestaurants || 20,
              progress: jobData.progress,
              message: jobData.progressMessage,
            })
          );
          
          // Handle completion
          if (jobData.status === "completed") {
            if (onComplete) {
              onComplete({
                jobId,
                totalProcessed: jobData.restaurantsProcessed || 0,
                veganResultsFound: jobData.veganResultsFound || 0,
              });
            }
            dispatch(adHocSearchComplete({
              jobId,
              totalProcessed: jobData.restaurantsProcessed || 0,
              veganResultsFound: jobData.veganResultsFound || 0,
            }));
            // Stop polling when complete
            if (pollIntervalRef.current) {
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
            }
          }
          
          // Handle cancellation
          if (jobData.status === "cancelled") {
            dispatch(stopAdHocSearch());
            dispatch(
              setAdHocSearchStatus("cancelled", jobId, {
                message: jobData.progressMessage || "Search cancelled",
              })
            );
            // Stop polling when cancelled
            if (pollIntervalRef.current) {
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
            }
          }
          
          // Handle failure
          if (jobData.status === "failed") {
            // Stop polling when failed
            if (pollIntervalRef.current) {
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
            }
          }
        }
      } catch (error) {
        console.error("Error polling job status:", error);
        // Don't stop polling on error - might be temporary network issue
      }
    };

    // Start polling as fallback (every 45 seconds - between 30-60 as requested)
    // Polling continues even when WebSocket is connected as a backup
    pollIntervalRef.current = setInterval(pollJobStatus, 45000);
    
    // Do an initial poll immediately
    pollJobStatus();

    // Cleanup on unmount
    return () => {
      if (socket) {
        socket.disconnect();
      }
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [jobId, autoConnect, dispatch, onRestaurantReceived, onPrompt, onComplete]);

  return {
    connected,
    restaurantsProcessed,
    veganResultsFound,
  };
}



